import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Copy, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Header } from '@/components/Header';
import { StatusBadge } from '@/components/StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Confirmacion() {
  const { ticket } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ticket) {
      axios.get(`${API}/solicitudes/ticket/${ticket}`)
        .then(r => setData(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [ticket]);

  const copyTicket = () => {
    navigator.clipboard.writeText(ticket);
    toast.success('Código copiado al portapapeles');
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin text-[#009940]" />
          </div>
        ) : (
          <div>
            {/* Success header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#009940]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={36} className="text-[#009940]" />
              </div>
              <h1 className="text-2xl font-semibold text-[#374B67] mb-2">¡Solicitud registrada!</h1>
              <p className="text-slate-500">Tu solicitud ha sido recibida correctamente. Guarda tu código de seguimiento.</p>
            </div>

            {/* Ticket card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 mb-6">
              <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider mb-3">Código de seguimiento</p>
              <div className="flex items-center justify-between bg-slate-50 border border-[#69C7EA]/40 rounded-xl px-6 py-5 mb-4">
                <span className="ticket-code text-2xl sm:text-3xl text-[#374B67]" data-testid="confirmation-ticket-code">{ticket}</span>
                <button
                  onClick={copyTicket}
                  className="flex items-center gap-1.5 text-sm text-[#374B67] hover:text-[#009940] font-medium transition-colors"
                  data-testid="copy-ticket-button"
                >
                  <Copy size={15} /> Copiar
                </button>
              </div>

              {data && (
                <div className="space-y-2.5">
                  {[
                    { label: 'Estado', value: <StatusBadge status={data.estado} /> },
                    { label: 'Zona', value: data.zona },
                    { label: 'Tipo de enseres', value: data.tipo_enseres },
                    { label: 'Fecha preferida', value: data.fecha_preferida },
                    { label: 'Fecha de solicitud', value: `${data.fecha_peticion} ${data.hora_peticion}` },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">{label}</span>
                      <span className="text-sm font-medium text-slate-900">{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-amber-800 mb-2">Próximos pasos</p>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Recibió un email de confirmación (si facilitó su correo)</li>
                <li>• Los enseres deben estar accesibles en la calle en la fecha indicada</li>
                <li>• La fecha está sujeta a disponibilidad del servicio</li>
                <li>• Puede consultar el estado en cualquier momento con su código</li>
              </ul>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to={`/consulta?ticket=${ticket}`}
                className="flex-1 flex items-center justify-center gap-2 bg-[#009940] hover:bg-[#007F35] text-white py-3 px-6 rounded-xl font-semibold text-sm transition-colors"
                data-testid="confirmation-consulta-link"
              >
                Ver estado de mi solicitud <ArrowRight size={16} />
              </Link>
              <Link
                to="/"
                className="flex-1 flex items-center justify-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-[#374B67] py-3 px-6 rounded-xl font-semibold text-sm transition-colors"
              >
                Volver al inicio
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
